//React
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import HoverTip, { HoverTipScope } from "../inputs/hoverTip";
import { formatValue, unitFor, unitOf } from "./formatters";
import { buildMatrix, cellAt } from "./matrixData";
//CSS
import "./charts.scss";

/**
 * A `matrix` dataset (ASB-4377, A.6): a 2-D grid with the `rows` column's
 * values down the side and the `cols` column's values across the top, each
 * header carrying the value's label, with its help on hover or focus (one
 * shared tooltip for the grid). A cell shows the summed `value`, its share of
 * the grand total and, with `detail`, the split by that column's labels, each
 * with its help on hover. When the dataset has a `drill` and the caller
 * passes `onDrill`, a cell whose rows agree on the drill keys, none of them
 * null, is a button (Enter and Space open it too) handing back
 * `{ key: value }` — so the detail labels inside it take no tab stop of their
 * own. A cell with a null drill key (A.7: the agreement cells of the LLM vs
 * rules matrix) is plain content, with no button role, cursor or key handler.
 */
function HeaderLabel({ header }) {
	return (
		<span className="as-matrix__header">
			{header.help ? <HoverTip content={header.help}>{header.label}</HoverTip> : header.label}
		</span>
	);
}

HeaderLabel.propTypes = {
	header: PropTypes.shape({
		value: PropTypes.string,
		label: PropTypes.string,
		help: PropTypes.string,
	}).isRequired,
};

function MatrixChart({ dataset, rows, onDrill, drillLabel }) {
	const matrix = useMemo(() => buildMatrix(dataset, rows ?? []), [dataset, rows]);
	if (!matrix.rowColumn || !matrix.colColumn || matrix.rows.length === 0) {
		return (
			<p className="as-chart__message">This dataset has no rows, cols and value columns to grid.</p>
		);
	}
	const canDrill = Boolean(dataset.drill && onDrill);
	const target = drillLabel ?? dataset.drill?.to ?? "";
	const open = (cell) => {
		if (!canDrill || !cell?.drill) return;
		onDrill(cell.drill);
	};
	const onKey = (cell) => (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			open(cell);
		}
	};
	// "1 NLP record in total", "97,186 NLP records in total".
	const unit =
		matrix.valueColumn.type === "int"
			? unitFor(matrix.valueColumn.label ?? "", matrix.total)
			: unitOf(matrix.valueColumn.label ?? "");

	return (
		<HoverTipScope>
			<div className="as-matrix-wrap">
				<table
					className="as-matrix"
					data-total={matrix.total}
				>
					<caption className="as-matrix__caption">
						{formatValue(matrix.valueColumn.type, matrix.total, "cell")} {unit} in total
					</caption>
					<thead>
						<tr>
							<th
								scope="col"
								className="as-matrix__corner"
							>
								<span className="as-matrix__axis as-matrix__axis--rows">
									<HeaderLabel header={matrix.rowColumn} />
								</span>
								<span className="as-matrix__axis as-matrix__axis--cols">
									<HeaderLabel header={matrix.colColumn} />
								</span>
							</th>
							{matrix.cols.map((col) => (
								<th
									key={col.value}
									scope="col"
									className="as-matrix__col"
									title={col.value !== col.label ? col.value : undefined}
								>
									<HeaderLabel header={col} />
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{matrix.rows.map((row) => (
							<tr key={row.value}>
								<th
									scope="row"
									className="as-matrix__row"
									title={row.value !== row.label ? row.value : undefined}
								>
									<HeaderLabel header={row} />
								</th>
								{matrix.cols.map((col) => {
									const cell = cellAt(matrix, row.value, col.value);
									const drill = canDrill && cell?.drill;
									const drillText = drill
										? Object.entries(cell.drill)
												.map(([k, v]) => `${k} ${v}`)
												.join(", ")
										: "";
									return (
										<td
											key={col.value}
											className="as-matrix__cell"
											data-row={row.value}
											data-col={col.value}
										>
											{cell ? (
												<div
													className={["as-matrix__value", drill ? "as-matrix__value--drill" : ""]
														.join(" ")
														.trim()}
													role={drill ? "button" : undefined}
													tabIndex={drill ? 0 : undefined}
													aria-label={
														drill
															? `Open ${target} for ${row.label} and ${col.label} (${drillText})`
															: undefined
													}
													onClick={drill ? () => open(cell) : undefined}
													onKeyDown={drill ? onKey(cell) : undefined}
												>
													<span className="as-matrix__count">
														{formatValue(matrix.valueColumn.type, cell.value, "cell")}
													</span>
													<span className="as-matrix__share">
														{cell.share === null
															? "—"
															: `${formatValue("pct", cell.share, "axis")} of total`}
													</span>
													{cell.details.length ? (
														<ul className="as-matrix__details">
															{cell.details.map((d) => (
																<li
																	key={d.value}
																	title={d.value !== d.label ? d.value : undefined}
																>
																	<span className="as-matrix__detail-n">
																		{formatValue(matrix.valueColumn.type, d.n, "cell")}
																	</span>{" "}
																	<span className="as-matrix__detail-label">
																		{d.help ? (
																			<HoverTip
																				content={d.help}
																				underline={false}
																				focusable={!drill}
																			>
																				{d.label}
																			</HoverTip>
																		) : (
																			d.label
																		)}
																	</span>
																</li>
															))}
														</ul>
													) : null}
												</div>
											) : (
												<span className="as-matrix__empty">—</span>
											)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</HoverTipScope>
	);
}

MatrixChart.propTypes = {
	/** a manifest dataset (design A.6) with chart "matrix", rows, cols, value, detail */
	dataset: PropTypes.shape({
		id: PropTypes.string,
		title: PropTypes.string,
		chart: PropTypes.oneOf(["matrix"]).isRequired,
		rows: PropTypes.string.isRequired,
		cols: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
		detail: PropTypes.string,
		columns: PropTypes.array,
		drill: PropTypes.shape({ to: PropTypes.string, on: PropTypes.arrayOf(PropTypes.string) }),
	}).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	/** ({ key: value }) => void, with the drill keys of the cell's rows */
	onDrill: PropTypes.func,
	/** the target dataset's title, for the cell's accessible name */
	drillLabel: PropTypes.string,
};

export default MatrixChart;
